import React from 'react';

import { useMatchMedia } from '@betterment/js-runtime';
import { Box, H3, Link, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { Flex } from '@betterment/style-closet-react';

import { useFooter } from './FooterProvider';

import { trackClick } from '../../features/track';
import brochureUrl from '../brochureUrl';
import { SUPPORT_EMAIL } from '../constants';

const footerTextStyles = {
  color: 'text.weak',
  fontSize: 'fontSize.50',
  lineHeight: 'lineHeight.50',
  '& > a': {
    textDecoration: 'underline',
    fontSize: 'fontSize.50',
    lineHeight: 'lineHeight.50',
    color: 'text.weak',
  },
};

// This style is used in multiple files, so we export it for reusability
export const FooterText = css(footerTextStyles);

const footerTypography = css({
  color: 'text.weak',
  fontSize: 'fontSize.50',
  lineHeight: 'lineHeight.50',
  '& span, & a, & h3': {
    color: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  '& span': { fontWeight: 'bold' },
  '& h3': { marginBottom: 'space.200', fontWeight: 'bold' },
  '& a': { textDecoration: 'underline' },
  '& p': footerTextStyles,
});

interface Props {
  showCashReserveDisclosure?: boolean;
  showFdicDisclosure?: boolean;
  showCheckingDisclosure?: boolean;
}

const Footer: React.FC<React.PropsWithChildren<Props>> = ({
  showCashReserveDisclosure = false,
  showFdicDisclosure = false,
  showCheckingDisclosure = false,
}) => {
  const { data } = useFooter();
  const isAtLeastMedium = useMatchMedia('atLeastMedium');

  const hasCR = showCashReserveDisclosure || showFdicDisclosure;
  const hasChecking = showCheckingDisclosure;

  return (
    <Box className={footerTypography}>
      <Box
        role="navigation"
        aria-label="Support options"
        css={{
          display: 'flex',
          gap: isAtLeastMedium ? 'space.400' : 'space.100',
          marginBottom: 'space.400',
        }}
      >
        <Text as="p" css={{ fontWeight: 'bold' }} aria-hidden={true}>
          Questions?
        </Text>
        <Box
          role="list"
          css={{
            display: 'flex',
            gap: isAtLeastMedium ? 'space.300' : 'space.100',
          }}
        >
          <Box role="listitem">
            <Link onClick={() => trackClick({ name: 'FAQ' })} target="_blank" href={brochureUrl('/help')}>
              Support Center
            </Link>
          </Box>
          <Box role="listitem">
            <Link onClick={() => trackClick({ name: 'ContactUs' })} target="_blank" href={brochureUrl('/contact')}>
              Contact Us
            </Link>
          </Box>
          <Box role="listitem">
            <Link href={`mailto:${SUPPORT_EMAIL}`}>Email Us</Link>
          </Box>
        </Box>
      </Box>
      {data && (
        <>
          {(hasCR || hasChecking) && (
            <Box role="list">
              {hasCR && (
                <Box
                  role="listitem"
                  css={{
                    paddingBottom: 'space.200',
                  }}
                >
                  <H3>Betterment Cash Reserve</H3>
                  {showCashReserveDisclosure && (
                    <Text
                      as="p"
                      css={{ marginBottom: 'space.200' }}
                      dangerouslySetInnerHTML={{ __html: data.terms.apyDailyRateDisclosure }}
                    />
                  )}
                  {showFdicDisclosure && (
                    <Text as="p" dangerouslySetInnerHTML={{ __html: data.terms.fdicDisclosure }} />
                  )}
                </Box>
              )}

              {hasChecking && (
                <Box
                  role="listitem"
                  css={{
                    paddingBottom: 'space.400',
                    marginBottom: 'space.500',
                    borderBottom: '1px solid',
                    borderColor: 'border',
                  }}
                >
                  <H3>Betterment Checking</H3>
                  <Flex mb="4">
                    <Text
                      as="p"
                      dangerouslySetInnerHTML={{
                        __html: `&Dagger; ${data.terms.checkingDisclosure}`,
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          )}

          <Flex flexDirection={['column', null, 'row']}>
            <Flex.Item pr="3" flex="1" mb="8">
              <Text
                as="p"
                css={{ marginBottom: 'space.200' }}
                dangerouslySetInnerHTML={{ __html: data.terms.advertisingDisclosure }}
              />
              <Box role="list">
                <Box role="listitem" css={{ marginBottom: 'space.200' }}>
                  <Text as="p" dangerouslySetInnerHTML={{ __html: data.terms.termsAndConditionsLink }} />
                </Box>
                <Box role="listitem" css={{ marginBottom: 'space.200' }}>
                  <Text as="p" dangerouslySetInnerHTML={{ __html: data.terms.privatePolicyLink }} />
                </Box>
                <Box role="listitem">
                  <Text as="p" dangerouslySetInnerHTML={{ __html: data.terms.trademarksLink }} />
                </Box>
              </Box>
            </Flex.Item>

            <Flex.Item flex="3">
              <Text
                as="p"
                css={{ marginBottom: 'space.100' }}
                dangerouslySetInnerHTML={{ __html: data.terms.investmentAdvisorDisclosure }}
              />
              <Text
                as="p"
                css={{ marginBottom: 'space.100' }}
                dangerouslySetInnerHTML={{ __html: data.terms.brokerageServicesDisclosure }}
              />
              <Text
                as="p"
                css={{ marginBottom: 'space.100' }}
                dangerouslySetInnerHTML={{ __html: data.terms.everydayCheckingDisclosure }}
              />
              <Text as="p" dangerouslySetInnerHTML={{ __html: data.terms.investmentInSecuritiesDisclosure }} />
            </Flex.Item>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default Footer;
